import React, { useEffect, useState, useMemo } from "react";
import Layout from "../components/Layout";
import { db, auth } from "../firebase";
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  addDoc, 
  setDoc, 
  doc, 
  getDoc,
  Timestamp 
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { FaFileExcel, FaPlus, FaUpload, FaSearch, FaHistory, FaTimes, FaBoxOpen } from "react-icons/fa";
import { updateDailyStock } from "../updateDailyStock";
import { logAudit } from "../auditLogger";

const statsCardStyle = {
  background: "#fff",
  padding: "20px",
  borderRadius: "12px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
  flex: 1,
};

const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0, 0, 0, 0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  backdropFilter: "blur(2px)",
};

const modalContentStyle = {
  background: "#fff",
  padding: "32px",
  borderRadius: "16px",
  width: "90%",
  maxWidth: "600px",
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
};

function WarehouseInward() {
  const [userProfile, setUserProfile] = useState(null);
  const today = new Date().toISOString().slice(0, 10);

  // States: Main Table
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [searchTerm, setSearchTerm] = useState("");
  const [userMap, setUserMap] = useState({});

  // States: Modals
  const [showManualModal, setShowManualModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  // States: SKU Metadata
  const [skuIndex, setSkuIndex] = useState({});
  const [skuList, setSkuList] = useState([]);

  // States: Manual Form
  const [manualForm, setManualForm] = useState({
    sku: "",
    quantity: "",
    rate: "",
    mrp: "",
    source: "ITC LTD",
    reference: ""
  });

  // States: Bulk Upload
  const [uploadPreview, setUploadPreview] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [skuMasterData, setSkuMasterData] = useState([]);

  // 0. Auth Check
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) setUserProfile(snap.data());
    });
    return () => unsub();
  }, []);

  // 1. Initial Load: SKUs and Users
  useEffect(() => {
    const loadMetadata = async () => {
      // Load Users
      const uSnap = await getDocs(collection(db, "users"));
      const uMap = {};
      uSnap.forEach(d => {
        const data = d.data();
        uMap[d.id] = data.name || data.displayName || data.email || d.id;
      });
      setUserMap(uMap);

      // Load SKUs
      const sSnap = await getDocs(query(collection(db, "skuMaster")));
      const skus = [];
      const sIdx = {};
      const sData = [];
      sSnap.forEach((d, i) => {
        const dData = d.data();
        const s = dData.sku;
        if (s) {
          skus.push(s);
          sIdx[s] = i;
          sData.push({ sku: s, mrp: dData.mrp || 0 });
        }
      });
      setSkuList(skus.sort());
      setSkuIndex(sIdx);
      setSkuMasterData(sData.sort((a,b) => a.sku.localeCompare(b.sku)));
    };
    loadMetadata().then(() => fetchInwardList());
  }, []);

  // 2. Fetch Inward Registry
  const fetchInwardList = async () => {
    setLoading(true);
    try {
      const qInward = query(
        collection(db, "inward_register"),
        where("inwardDate", ">=", fromDate),
        where("inwardDate", "<=", toDate),
        orderBy("inwardDate", "desc")
      );
      const snap = await getDocs(qInward);
      const dataMap = {};
      snap.forEach(dDoc => {
        const d = dDoc.data();
        // Include MRP in key to show separate lines if MRP differs
        const mVal = Number(d.mrp || 0);
        const key = `${d.inwardDate}_${d.sku}_${mVal}`;
        
        if (!dataMap[key]) {
          dataMap[key] = { 
            date: d.inwardDate, 
            sku: d.sku, 
            qty: 0, 
            rate: d.purchaseBasicRate || 0,
            mrp: mVal,
            source: d.source || "",
            uploadedBy: d.uploadedBy || ""
          };
        }
        dataMap[key].qty += Number(d.quantity || 0);
      });
      
      const sorted = Object.values(dataMap).sort((a, b) => {
        if (a.date !== b.date) return b.date.localeCompare(a.date);
        return (skuIndex[a.sku] ?? 9999) - (skuIndex[b.sku] ?? 9999);
      });
      setRows(sorted);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 3. Manual Inward Logic
  const handleManualSave = async () => {
    if (!manualForm.sku || !manualForm.quantity || !manualForm.mrp) return alert("Fill required fields (SKU, Qty, MRP)");
    setLoading(true);
    try {
      await addDoc(collection(db, "inward_register"), {
        clientId: userProfile?.clientId || "mdsons",
        inwardDate: today,
        sku: manualForm.sku,
        quantity: Number(manualForm.quantity),
        purchaseBasicRate: Number(manualForm.rate) || 0,
        mrp: Number(manualForm.mrp) || 0,
        source: manualForm.source,
        reference: manualForm.reference,
        uploadedBy: auth.currentUser?.uid || "system",
        uploadedAt: Timestamp.now(),
        editable: true
      });
      await updateDailyStock(today, manualForm.sku);
      alert("Manual entry saved");
      setShowManualModal(false);
      setManualForm({ sku: "", quantity: "", rate: "", mrp: "", source: "ITC LTD", reference: "" });
      fetchInwardList();
    } catch (err) {
      alert("Error saving: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 4. CSV Upload Logic
  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const map = {};
        results.data.forEach(r => {
          const s = (r.SKU || r.sku || "").trim();
          const q = Number(r.Qty || r.quantity || 0);
          const rt = Number(r.Rate || r.rate || 0);
          const m = Number(r.MRP || r.mrp || 0);

          if (s && q > 0) {
            // Distinguish by SKU + MRP for aggregation if multiple rows exist
            const key = `${s}_${m}`;
            if (!map[key]) {
              map[key] = { sku: s, quantity: 0, value: 0, mrp: m };
            }
            map[key].quantity += q;
            map[key].value += q * rt;
          }
        });
        setUploadPreview(Object.values(map).map(it => ({
          ...it, rate: it.quantity > 0 ? it.value / it.quantity : 0
        })));
      }
    });
  };

  const confirmBulkUpload = async () => {
    setUploading(true);
    try {
      for (const it of uploadPreview) {
        const docId = `${today}_${it.sku}_${it.mrp}`;
        await setDoc(doc(db, "inward_register", docId), {
          clientId: userProfile?.clientId || "mdsons",
          inwardDate: today,
          sku: it.sku,
          quantity: it.quantity,
          purchaseBasicRate: it.rate,
          mrp: it.mrp,
          source: "ITC LTD",
          reference: "Bulk CSV Upload",
          uploadedBy: auth.currentUser?.uid || "system",
          uploadedAt: Timestamp.now(),
          editable: true
        }, { merge: true });
        
        await updateDailyStock(today, it.sku);
        await logAudit({
          module: "WAREHOUSE",
          action: "INWARD_UPLOAD",
          refCollection: "inward_register",
          refDocId: docId,
          sku: it.sku,
          date: today,
          newData: it, role: "OPERATOR"
        });
      }
      alert("Bulk upload completed!");
      setUploadPreview([]);
      setShowUploadModal(false);
      fetchInwardList();
    } catch (err) {
      console.error(err);
      alert("Error uploading: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  // 5. Export Logic
  const exportRegistry = () => {
    const data = filteredRows.map(r => ({
      Date: r.date,
      SKU: r.sku,
      Quantity: r.qty,
      Rate: Number(r.rate.toFixed(2)),
      MRP: Number(r.mrp.toFixed(2)),
      Source: r.source,
      "Uploaded By": userMap[r.uploadedBy] || r.uploadedBy
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "InwardRegistry");
    XLSX.writeFile(wb, `Inward_Registry_${fromDate}_to_${toDate}.xlsx`);
  };

  // Computed
  const filteredRows = rows.filter(r => 
    r.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = useMemo(() => {
    const totalQty = rows.reduce((acc, curr) => acc + curr.qty, 0);
    const todayRows = rows.filter(r => r.date === today);
    const todayTotal = todayRows.reduce((acc, curr) => acc + curr.qty, 0);
    return { totalQty, todayTotal, todayCount: todayRows.length };
  }, [rows, today]);

  return (
    <Layout>
      <div style={{ padding: "40px 30px", background: "#f9fafb", minHeight: "100vh" }}>
        
        {/* HEADER SECTION */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 32 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 32, color: "#111827", fontWeight: 800 }}>Inward Management</h1>
            <p style={{ marginTop: 8, color: "#6b7280", fontSize: 15 }}>Consolidated warehouse inward management command center.</p>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button 
              onClick={() => setShowManualModal(true)}
              style={{ padding: "12px 20px", background: "#111827", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
            >
              <FaPlus /> Manual Entry
            </button>
            <button 
              onClick={() => setShowUploadModal(true)}
              style={{ padding: "12px 20px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
            >
              <FaUpload /> Bulk Upload
            </button>
          </div>
        </div>

        {/* STATS STRIP */}
        <div style={{ display: "flex", gap: 24, marginBottom: 32 }}>
          <div style={statsCardStyle}>
            <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 600, marginBottom: 8, textTransform: "uppercase" }}>Register Sum</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#111827" }}>{stats.totalQty.toLocaleString()} <span style={{ fontSize: 14, fontWeight: 400 }}>Total Qty</span></div>
          </div>
          <div style={statsCardStyle}>
            <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 600, marginBottom: 8, textTransform: "uppercase" }}>Today's Inward</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#047857" }}>{stats.todayTotal.toLocaleString()} <span style={{ fontSize: 14, fontWeight: 400 }}>Total Qty</span></div>
          </div>
          <div style={statsCardStyle}>
            <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 600, marginBottom: 8, textTransform: "uppercase" }}>Batches Today</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#ea580c" }}>{stats.todayCount} <span style={{ fontSize: 14, fontWeight: 400 }}>SKUs</span></div>
          </div>
        </div>

        {/* FILTERS & SEARCH */}
        <div style={{ background: "#fff", padding: "20px", borderRadius: 12, border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 20, marginBottom: 24 }}>
          <div style={{ position: "relative", flex: 1 }}>
            <FaSearch style={{ position: "absolute", left: 14, top: 13, color: "#9ca3af" }} />
            <input 
              placeholder="Search by SKU Code..." 
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: "100%", padding: "10px 10px 10px 40px", borderRadius: 8, border: "1px solid #d1d5db", outline: "none" }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#4b5563" }}>FROM</span>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db" }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: "#4b5563" }}>TO</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db" }} />
          </div>
          <button onClick={fetchInwardList} style={{ padding: "10px 20px", background: "#f3f4f6", borderRadius: 8, border: "1px solid #d1d5db", cursor: "pointer", fontWeight: 600 }}>Apply</button>
          <button onClick={exportRegistry} style={{ padding: "10px 20px", background: "#166534", color: "#fff", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
            <FaFileExcel /> Export Excel
          </button>
        </div>

        {/* DATA TABLE */}
        <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead style={{ background: "#f9fafb" }}>
              <tr>
                <th style={{ padding: "16px 20px", fontSize: 12, fontWeight: 700, color: "#4b5563", borderBottom: "1px solid #f3f4f6" }}>DATE</th>
                <th style={{ padding: "16px 20px", fontSize: 12, fontWeight: 700, color: "#4b5563", borderBottom: "1px solid #f3f4f6" }}>SKU MATERIAL</th>
                <th style={{ padding: "16px 20px", fontSize: 12, fontWeight: 700, color: "#4b5563", borderBottom: "1px solid #f3f4f6", textAlign: "right" }}>QUANTITY</th>
                <th style={{ padding: "16px 20px", fontSize: 12, fontWeight: 700, color: "#4b5563", borderBottom: "1px solid #f3f4f6", textAlign: "right" }}>AVG RATE (₹)</th>
                <th style={{ padding: "16px 20px", fontSize: 12, fontWeight: 700, color: "#4b5563", borderBottom: "1px solid #f3f4f6", textAlign: "right" }}>MRP (₹)</th>
                <th style={{ padding: "16px 20px", fontSize: 12, fontWeight: 700, color: "#4b5563", borderBottom: "1px solid #f3f4f6" }}>VENDOR/SOURCE</th>
                <th style={{ padding: "16px 20px", fontSize: 12, fontWeight: 700, color: "#4b5563", borderBottom: "1px solid #f3f4f6" }}>UPLOADED BY</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>Fetching register data...</td></tr>
              ) : filteredRows.length === 0 ? (
                <tr><td colSpan="7" style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>No inward records found for this period.</td></tr>
              ) : (
                filteredRows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "16px 20px", fontSize: 14 }}>
                      <div style={{ fontWeight: 600 }}>{r.date.split('-').reverse().join('-')}</div>
                    </td>
                    <td style={{ padding: "16px 20px", fontSize: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 32, height: 32, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, color: "#16a34a" }}><FaBoxOpen /></div>
                        <span style={{ fontWeight: 600 }}>{r.sku}</span>
                      </div>
                    </td>
                    <td style={{ padding: "16px 20px", fontSize: 14, textAlign: "right", fontWeight: 700 }}>{r.qty.toLocaleString()}</td>
                    <td style={{ padding: "16px 20px", fontSize: 14, textAlign: "right", color: "#4b5563" }}>₹{r.rate.toFixed(2)}</td>
                    <td style={{ padding: "16px 20px", fontSize: 14, textAlign: "right", color: "#4b5563" }}>₹{r.mrp.toFixed(2)}</td>
                    <td style={{ padding: "16px 20px", fontSize: 14 }}>
                      <span style={{ padding: "4px 8px", background: "#eff6ff", color: "#2563eb", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{r.source}</span>
                    </td>
                    <td style={{ padding: "16px 20px", fontSize: 13, color: "#6b7280" }}>{userMap[r.uploadedBy] || r.uploadedBy}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* MODAL: MANUAL INWARD */}
        {showManualModal && (
          <div style={modalOverlayStyle}>
            <div style={modalContentStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                <h3 style={{ margin: 0, fontSize: 20 }}>Manual Warehouse Inward</h3>
                <button onClick={() => setShowManualModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9ca3af" }}><FaTimes /></button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                <div style={{ gridColumn: "span 2" }}>
                  <label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 600 }}>Select Material SKU <span style={{ color: "red" }}>*</span></label>
                  <select 
                    value={manualForm.sku} 
                    onChange={e => setManualForm({...manualForm, sku: e.target.value})}
                    style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #d1d5db", outline: "none" }}
                  >
                    <option value="">-- Search and Select SKU --</option>
                    {skuList.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 600 }}>Quantity <span style={{ color: "red" }}>*</span></label>
                  <input type="number" value={manualForm.quantity} onChange={e => setManualForm({...manualForm, quantity: e.target.value})} style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #d1d5db" }} />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 600 }}>MRP (₹) <span style={{ color: "red" }}>*</span></label>
                  <input type="number" value={manualForm.mrp} onChange={e => setManualForm({...manualForm, mrp: e.target.value})} style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #d1d5db" }} />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 600 }}>Basic Rate (Purchase ₹)</label>
                  <input type="number" value={manualForm.rate} onChange={e => setManualForm({...manualForm, rate: e.target.value})} style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #d1d5db" }} />
                </div>
                <div>
                  <label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 600 }}>Source</label>
                  <select value={manualForm.source} onChange={e => setManualForm({...manualForm, source: e.target.value})} style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #d1d5db" }}>
                    <option value="ITC LTD">ITC LTD (DIRECT)</option>
                    <option value="WAREHOUSE TRANSFER">WAREHOUSE TRANSFER</option>
                  </select>
                </div>
                <div style={{ gridColumn: "span 2" }}>
                  <label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 600 }}>Ref. Number</label>
                  <input value={manualForm.reference} onChange={e => setManualForm({...manualForm, reference: e.target.value})} placeholder="Invoice/LR No." style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #d1d5db" }} />
                </div>
              </div>
              <button 
                onClick={handleManualSave}
                style={{ width: "100%", marginTop: 32, padding: 16, background: "#111827", color: "#fff", border: "none", borderRadius: 12, fontWeight: 700, cursor: "pointer" }}
              >
                {loading ? "Processing..." : "Commit Inward Entry"}
              </button>
            </div>
          </div>
        )}

        {/* MODAL: BULK UPLOAD */}
        {showUploadModal && (
          <div style={modalOverlayStyle}>
            <div style={modalContentStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                <h3 style={{ margin: 0, fontSize: 20 }}>Bulk Inward Intake (CSV)</h3>
                <button onClick={() => setShowUploadModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#9ca3af" }}><FaTimes /></button>
              </div>
              <div style={{ padding: 20, border: "2px dashed #d1d5db", borderRadius: 12, textAlign: "center", marginBottom: 12 }}>
                <input type="file" accept=".csv" onChange={handleCSVUpload} style={{ display: "none" }} id="csvFileInput" />
                <label htmlFor="csvFileInput" style={{ cursor: "pointer" }}>
                  <FaUpload style={{ fontSize: 32, color: "#2563eb", marginBottom: 12 }} />
                  <div style={{ fontWeight: 600, color: "#111827" }}>Click to select CSV File</div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Format: SKU, Qty, Rate, MRP</div>
                </label>
              </div>

              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <button 
                  onClick={() => {
                    const headers = "SKU,Qty,Rate,MRP\n";
                    const rows = skuMasterData.map(it => `${it.sku},0,0,${it.mrp || 0}`).join("\n");
                    const csvString = headers + rows;
                    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
                    const link = document.createElement("a");
                    const url = URL.createObjectURL(blob);
                    link.setAttribute("href", url);
                    link.setAttribute("download", `inward_template_${today}.csv`);
                    link.style.visibility = "hidden";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  style={{ background: "none", border: "none", color: "#2563eb", fontSize: 13, fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                >
                  📥 Download Pre-filled Template (All SKUs)
                </button>
              </div>

              {uploadPreview.length > 0 && (
                <div>
                  <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
                    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                      <thead style={{ background: "#f9fafb" }}>
                        <tr><th style={{ padding: 8 }}>SKU</th><th style={{ textAlign: "right", padding: 8 }}>Qty</th><th style={{ textAlign: "right", padding: 8 }}>Rate</th><th style={{ textAlign: "right", padding: 8 }}>MRP</th></tr>
                      </thead>
                      <tbody>
                        {uploadPreview.map((p, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}><td style={{ padding: 8 }}>{p.sku}</td><td style={{ textAlign: "right", padding: 8 }}>{p.quantity}</td><td style={{ textAlign: "right", padding: 8 }}>₹{p.rate.toFixed(2)}</td><td style={{ textAlign: "right", padding: 8 }}>₹{p.mrp.toFixed(2)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button 
                    onClick={confirmBulkUpload}
                    disabled={uploading}
                    style={{ width: "100%", marginTop: 24, padding: 16, background: "#2563eb", color: "#fff", border: "none", borderRadius: 12, fontWeight: 700, cursor: "pointer" }}
                  >
                    {uploading ? "Uploading Register..." : `Import ${uploadPreview.length} Material Records`}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}

export default WarehouseInward;
